// Selection rules for the spacing measurement (EVA-29). These live here rather
// than inline in XdaViewer so the interaction layer is testable against the
// shipped functions instead of a copy of them — same reasoning as
// `spacingKeyboard.ts`.

/**
 * Which entry a click leaves pinned: clicking the pinned well releases it,
 * clicking any other well re-pins to that one.
 */
export const nextPinnedEntryIndex = (
  pinned: Nullable<number>,
  clicked: number
): Nullable<number> => (pinned === clicked ? null : clicked);

/**
 * The entry the overlay draws from. A pin wins over a hover preview, so
 * hovering other wells cannot disturb a pinned display; with neither, there is
 * nothing to draw.
 */
export const resolveSpacingAnchor = (
  pinned: Nullable<number>,
  hovered: Nullable<number>
): Nullable<number> => pinned ?? hovered;

/**
 * The entry a host pins on a well's behalf, rather than the user clicking the
 * plot for it: the well card panel is already about one well, so that well is
 * the reference the measurements are taken from the moment the section draws.
 *
 * The FIRST intercept of the well is taken. A U-shape well repeats its UWI
 * across several entries and each is independent, but nothing outside the plot
 * says which of them the host means — and the alternative, pinning none, is a
 * section with no measurements on it at all.
 */
export const resolveEntryIndexForUwi = (
  ids: Maybe<readonly { unique_id: string }[]>,
  uwi: Maybe<string>
): Nullable<number> => {
  if (!uwi || !ids?.length) {
    return null;
  }
  const found = ids.findIndex((id) => id?.unique_id === uwi);
  return found === -1 ? null : found;
};

/**
 * Re-resolve a pinned entry against a fresh `/line` response.
 *
 * Entry indexes are only meaningful within one response's ordering, so a pin
 * cannot simply be carried over — but dropping it on every refetch loses the
 * selection for reloads that do not change the well set at all. Toggling "Hang
 * to Top Zone" is exactly that case: it re-datums wells vertically and refetches
 * while the same wells, in the same order, come back.
 *
 * The pin is therefore matched by identity — the well's UWI plus WHICH of its
 * intercepts was pinned (U-shape wells repeat a UWI, and those siblings are
 * independent entries) — and released only when that well is no longer in the
 * response.
 */
export const resolvePinnedEntryAfterReload = (
  previousIds: Maybe<readonly { unique_id: string }[]>,
  nextIds: Maybe<readonly { unique_id: string }[]>,
  pinnedIndex: Nullable<number>
): Nullable<number> => {
  if (pinnedIndex == null || !previousIds?.length || !nextIds?.length) {
    return null;
  }
  const pinnedUwi = previousIds[pinnedIndex]?.unique_id;
  if (!pinnedUwi) {
    return null;
  }
  // Which intercept of this well the pin was on.
  let ordinal = 0;
  for (let i = 0; i < pinnedIndex; i++) {
    if (previousIds[i]?.unique_id === pinnedUwi) {
      ordinal++;
    }
  }
  let seen = 0;
  for (let i = 0; i < nextIds.length; i++) {
    if (nextIds[i]?.unique_id !== pinnedUwi) {
      continue;
    }
    if (seen === ordinal) {
      return i;
    }
    seen++;
  }
  return null;
};
